import { capitalize } from 'util/strings'

export function isPlanLimitsExceeded(plan, usage) {
  const productFeatures = plan.features
  const featureLookup = Object.keys(productFeatures)
  const productUsage = usage
  return Object.keys(productUsage).some(key => {
    const usageValue = productUsage[key]
    // Eg custom_profile, rules
    if (
      featureLookup.includes(key) &&
      typeof usageValue === 'boolean' &&
      usageValue &&
      !productFeatures[key]
    ) {
      return true
    }

    // Eg teams
    if (
      featureLookup.includes(key) &&
      typeof usageValue === 'number' &&
      usageValue > 0 &&
      !productFeatures[key]
    ) {
      return true
    }

    // Eg max_channels, max_integrations, max_kbs
    const featureKey = `max${capitalize(key)}`
    if (
      featureLookup.includes(featureKey) &&
      typeof usageValue === 'number' &&
      productFeatures[featureKey] !== 'unlimited' &&
      usageValue > productFeatures[featureKey]
    ) {
      return true
    }

    return false
  })
}

export function isAllowedAccessToFeature(plan, feature) {
  if (!plan || !plan.features || !feature) return true
  return !!plan.features[feature]
}

export function isPlanSmaller(currentPlan, newPlan) {
  if (currentPlan.version !== newPlan.version) return false
  return currentPlan.monthlyPrice >= newPlan.monthlyPrice
}

export const buildLineItemFromPlan = (plan, billingCycle, quantity = 1) => {
  const { minimumQuantity, pricingModel } = plan

  let cyclePrice =
    billingCycle === 'annual' ? plan.annualPrice : plan.monthlyPrice
  if (pricingModel !== 'flat') {
    cyclePrice *= Math.max(minimumQuantity, quantity)
  }

  return {
    ...plan,
    id: plan.planId,
    cyclePrice,
    itemPrice: billingCycle === 'annual' ? plan.annualPrice : plan.monthlyPrice,
    minimumQuantity,
    quantity,
  }
}

export const buildLineItemsFromPlans = ({
  inboxPlanId,
  kbPlanId,
  billingCycle,
  plans,
  usage,
  provider,
}) => {
  const inboxPlan = plans.find(
    p => p.product === 'inbox' && p.planId === inboxPlanId
  )
  const kbPlan = plans.find(p => p.product === 'kb' && p.planId === kbPlanId)
  if (inboxPlan.pricingModel === 'flat') {
    return [buildLineItemFromPlan(inboxPlan, billingCycle, usage.inbox.users)]
  }

  const lineItems = [
    buildLineItemFromPlan(inboxPlan, billingCycle, usage.inbox.users),
  ]

  if (provider !== 'shopify') {
    lineItems.push(buildLineItemFromPlan(kbPlan, billingCycle, 1))
  }

  return lineItems
}

export const calculateSubscriptionFlatTotal = (planLineItems = []) => {
  return planLineItems
    .filter(r => r.pricingModel === 'flat')
    .reduce((total, row) => {
      // eslint-disable-next-line no-param-reassign
      total += row.cyclePrice
      return total
    }, 0)
}

export const calculateSubscriptionUsageTotal = (planLineItems = []) => {
  return planLineItems
    .filter(r => r.pricingModel === 'usage')
    .reduce((total, row) => {
      // eslint-disable-next-line no-param-reassign
      total += row.cyclePrice
      return total
    }, 0)
}

export const calculateSubscriptionMaxUsageAmount = (planLineItems = []) => {
  return calculateSubscriptionUsageTotal(planLineItems) * 100
}

const splitPlanIntoVersionAndPlan = plan => {
  if (!plan) return { version: 1, plan: '' }
  const matches = /v(\d+)-(.*)/g.exec(plan.value)
  if (!matches) return { version: 1, plan: plan.value }
  const [, version, planPart] = matches
  return {
    version: parseInt(version, 10),
    plan: planPart,
  }
}

export const isVersionUpgrade = (newPlan, currentPlan) => {
  const {
    version: newVersion,
    plan: newPlanPart,
  } = splitPlanIntoVersionAndPlan(newPlan)
  const {
    version: currentVersion,
    plan: currentPlanPart,
  } = splitPlanIntoVersionAndPlan(currentPlan)
  if (newVersion > currentVersion && newPlanPart === currentPlanPart)
    return true
  return false
}

export const getPlanSupportedCycles = (product, plan, plans) => {
  const planInfo = plans.find(p => p.product === product && p.value === plan)
  if (
    // If we cant find the plan info, assume it supports monthly and annual
    !planInfo ||
    // If the plan is completely free, then it supports both annual and monthly (kb-basic)
    (planInfo.monthly === 0 && planInfo.annual === 0) ||
    // If it has a month and annual price, then it supports both annual and monthly
    (planInfo.monthly !== 0 && planInfo.annual !== 0)
  )
    return 'all'
  if (planInfo.monthly === 0) return 'annual'
  return 'monthly'
}
